import React, { useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  FormControlLabel,
  IconButton,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import { useTranslation } from 'react-i18next';
import ConfirmDialog from './ConfirmDialog';
import {
  getExtraHoursEndOptions,
  getExtraHoursStartOptions,
} from '../../utils/authorizationHoursTimeOptions';
import {
  TIME_OPTIONS,
  WEEK_DAYS,
  buildFollowingPeriod,
  cloneDayHours,
  getPeriodsErrorCode,
} from '../../utils/weeklyHoursUtils';
import { BRAND, brandAlpha } from '../../theme/brandTokens';

const CLOSED_APPLY_DIALOG = { open: false, sourceDay: null, sourceHours: null };

const formatPeriods = (periods) => periods.map((period) => `${period.from} – ${period.to}`).join(', ');

const WeeklyHoursEditor = ({
  value,
  onChange,
  canUpdate,
  referencePeriodsByDay = {},
  referenceLabel,
  blockReferenceOverlap = false,
  nextPeriodFor = buildFollowingPeriod,
  applyAllFieldLabel,
  onValidationError,
}) => {
  const { t } = useTranslation();
  const [applyDialog, setApplyDialog] = useState(CLOSED_APPLY_DIALOG);

  const referencePeriodsFor = (day) => referencePeriodsByDay[day] || [];
  const blockedPeriodsFor = (day) => (blockReferenceOverlap ? referencePeriodsFor(day) : []);

  const validateDayPeriods = (day, periods) => {
    const errorCode = getPeriodsErrorCode(periods, blockedPeriodsFor(day));
    if (errorCode) {
      onValidationError?.(errorCode);
      return false;
    }
    return true;
  };

  const updateDayHours = (day, nextDayHours, promptApply) => {
    onChange({ ...value, [day]: nextDayHours });
    if (promptApply && !nextDayHours.closed && WEEK_DAYS.some((other) => other !== day && !value[other].closed)) {
      setApplyDialog({ open: true, sourceDay: day, sourceHours: cloneDayHours(nextDayHours) });
    }
  };

  const updatePeriodTime = (day, dayHours, periodIndex, field, time) => {
    const periods = dayHours.periods.map((item, index) => (
      index === periodIndex ? { ...item, [field]: time } : item
    ));
    if (validateDayPeriods(day, periods)) {
      updateDayHours(day, { ...dayHours, periods }, true);
    }
  };

  const addPeriod = (day, dayHours) => {
    const last = dayHours.periods[dayHours.periods.length - 1];
    const periods = [...dayHours.periods, nextPeriodFor(last)];
    if (validateDayPeriods(day, periods)) {
      updateDayHours(day, { ...dayHours, periods }, true);
    }
  };

  const closeApplyDialog = () => setApplyDialog(CLOSED_APPLY_DIALOG);

  const applyToAllOpenDays = () => {
    if (applyDialog.sourceHours) {
      onChange(Object.fromEntries(
        WEEK_DAYS.map((day) => [
          day,
          value[day].closed ? cloneDayHours(value[day]) : cloneDayHours(applyDialog.sourceHours),
        ]),
      ));
    }
    closeApplyDialog();
  };

  const dayName = (day) => (day ? t(`common.days.${day}`) : '');

  return (
    <>
      <Box
        sx={{
          border: `1px solid ${BRAND.line}`,
          borderRadius: 2,
          backgroundColor: BRAND.background,
          mb: 3,
        }}
      >
        {WEEK_DAYS.map((day) => {
          const dayHours = value[day];
          const referencePeriods = referencePeriodsFor(day);
          return (
            <Box
              key={day}
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                gap: { xs: 1, sm: 2 },
                p: 2,
                borderBottom: `1px solid ${BRAND.lineSoft}`,
                backgroundColor: BRAND.surface,
                '&:last-child': { borderBottom: 'none' },
              }}
            >
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, minWidth: { sm: 200 } }}>
                <FormControlLabel
                  sx={{ m: 0 }}
                  control={(
                    <Checkbox
                      checked={!dayHours.closed}
                      disabled={!canUpdate}
                      onChange={(event) => updateDayHours(
                        day,
                        { ...dayHours, closed: !event.target.checked },
                        event.target.checked,
                      )}
                      sx={{ color: BRAND.accentText, '&.Mui-checked': { color: BRAND.accentText } }}
                    />
                  )}
                  label={(
                    <Typography sx={{ fontWeight: 600, textTransform: 'capitalize' }}>
                      {dayName(day)}
                    </Typography>
                  )}
                />
                {referenceLabel ? (
                  <Box sx={{ pl: '42px', display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center' }}>
                    <Typography variant="caption" sx={{ color: BRAND.textMuted, mr: 0.5, lineHeight: '20px', flexShrink: 0 }}>
                      {referenceLabel}:
                    </Typography>
                    {referencePeriods.length > 0 ? referencePeriods.map((period) => (
                      <Chip
                        key={`${period.from}-${period.to}`}
                        label={`${period.from} – ${period.to}`}
                        size="small"
                        sx={{
                          fontSize: '0.7rem',
                          height: 20,
                          backgroundColor: brandAlpha('accent', 0.10),
                          color: BRAND.accentText,
                          fontWeight: 500,
                          border: `1px solid ${brandAlpha('accent', 0.25)}`,
                          '& .MuiChip-label': { px: 1 },
                        }}
                      />
                    )) : (
                      <Typography variant="caption" sx={{ color: BRAND.textMuted, fontStyle: 'italic', lineHeight: '20px' }}>
                        {t('common.closed')}
                      </Typography>
                    )}
                  </Box>
                ) : null}
              </Box>

              {dayHours.closed ? null : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, flex: 1 }}>
                  {dayHours.periods.map((period, periodIndex) => {
                    const otherPeriods = dayHours.periods.filter((_, index) => index !== periodIndex);
                    const startOptions = getExtraHoursStartOptions(TIME_OPTIONS, period, blockedPeriodsFor(day), otherPeriods);
                    const endOptions = getExtraHoursEndOptions(TIME_OPTIONS, period, blockedPeriodsFor(day), otherPeriods);

                    return (
                      <Box key={`${day}-${periodIndex}`} sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                        <Select
                          value={period.from}
                          disabled={!canUpdate}
                          onChange={(event) => updatePeriodTime(day, dayHours, periodIndex, 'from', event.target.value)}
                          MenuProps={{ disableScrollLock: true }}
                          size="small"
                          sx={{ width: 140 }}
                          inputProps={{ 'aria-label': `${dayName(day)} ${t('stores.timePricing.from')}` }}
                        >
                          {startOptions.map((option) => (
                            <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                          ))}
                        </Select>
                        <Select
                          value={period.to}
                          disabled={!canUpdate}
                          onChange={(event) => updatePeriodTime(day, dayHours, periodIndex, 'to', event.target.value)}
                          MenuProps={{ disableScrollLock: true }}
                          size="small"
                          sx={{ width: 140 }}
                          inputProps={{ 'aria-label': `${dayName(day)} ${t('stores.timePricing.to')}` }}
                        >
                          {endOptions.map((option) => (
                            <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                          ))}
                        </Select>
                        {canUpdate && dayHours.periods.length > 1 && (
                          <IconButton
                            size="small"
                            aria-label={`${dayName(day)} ${t('common.delete')}`}
                            onClick={() => updateDayHours(
                              day,
                              { ...dayHours, periods: dayHours.periods.filter((_, index) => index !== periodIndex) },
                              true,
                            )}
                            sx={{ color: '#c0392b' }}
                          >
                            <RemoveIcon fontSize="small" />
                          </IconButton>
                        )}
                      </Box>
                    );
                  })}
                  {canUpdate && (
                    <Button
                      size="small"
                      startIcon={<AddIcon />}
                      onClick={() => addPeriod(day, dayHours)}
                      sx={{ alignSelf: 'flex-start', color: BRAND.accentText, fontWeight: 600, textTransform: 'none' }}
                    >
                      {t('storeRegistration.businessHours.addPeriod')}
                    </Button>
                  )}
                </Box>
              )}
            </Box>
          );
        })}
      </Box>

      <ConfirmDialog
        open={applyDialog.open}
        onClose={closeApplyDialog}
        onConfirm={applyToAllOpenDays}
        title={t('storeRegistration.businessHours.applyToAllTitle')}
        message={t('storeRegistration.businessHours.applyToAllMessage', {
          field: applyAllFieldLabel,
          day: dayName(applyDialog.sourceDay),
          value: applyDialog.sourceHours ? formatPeriods(applyDialog.sourceHours.periods) : '',
        })}
        cancelText={t('storeRegistration.businessHours.applyToCurrentDay', { day: dayName(applyDialog.sourceDay) })}
        confirmText={t('storeRegistration.businessHours.applyToAllDays')}
        confirmColor="primary"
      />
    </>
  );
};

export default WeeklyHoursEditor;
